import { motion } from "framer-motion";
import { Unlock, Compass, TrendingUp } from "lucide-react";
import { Button } from "../ui/button";
import analytics from "../../../../libs/analytics";

const Solution = () => {
  const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: Unlock,
      title: "DESTRAVE",
      description: "Pratique em um ambiente seguro onde errar faz parte do processo e falar vira natural.",
      color: "primary",
      iconAnimation: {
        rotate: [0, -15, 0],
        y: [0, -3, 0],
      },
    },
    {
      icon: Compass,
      title: "GUIA",
      description: "Cada sessão é estruturada para desenvolver habilidades reais de comunicação e apenas não conversa solta.",
      color: "accent",
      iconAnimation: {
        rotate: [0, 360],
      },
    },
    {
      icon: TrendingUp,
      title: "EVOLUA",
      description: "Acompanhe sua evolução pelo que você já consegue comunicar, não pelo tempo que passou falando.",
      color: "primary",
      iconAnimation: {
        y: [0, -8, 0],
        scale: [1, 1.1, 1],
      },
    },
  ];

  return (
    <section id="oq-e-o-ataric" className="py-20 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 hero-gradient" />
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Ataric não ensina inglês.{" "}
            <span className="text-gradient glow-text">Ataric te faz falar.</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Um ecossistema de prática estruturada por voz que guia, corrige e acompanha sua evolução em situações reais.
          </p>
          <Button type="button" variant="cta" size="lg" className="mt-6" onClick={(e) => { analytics.landingCtaClicked('solution'); scrollToSection(e, 'funcionamento'); }}>
            Conheça o app
          </Button>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="group"
            >
              <div className="card-glass rounded-2xl p-8 h-full hover:border-primary/50 transition-all duration-300 hover:shadow-[0_0_40px_hsl(277_78%_40%/0.3)]">
                <div className={`w-16 h-16 rounded-2xl mb-6 flex items-center justify-center ${
                  feature.color === "primary" 
                    ? "bg-primary/20 group-hover:bg-primary/30" 
                    : "bg-accent/20 group-hover:bg-accent/30"
                } transition-colors`}>
                  <motion.div
                    animate={feature.iconAnimation}
                    transition={{ 
                      duration: index === 1 ? 8 : 2, 
                      repeat: Infinity, 
                      ease: index === 1 ? "linear" : "easeInOut" 
                    }}
                  >
                    <feature.icon className={`w-8 h-8 ${
                      feature.color === "primary" ? "text-primary" : "text-accent"
                    }`} />
                  </motion.div>
                </div>
                <h3 className={`font-display text-2xl font-bold mb-4 ${
                  feature.color === "primary" ? "text-primary" : "text-accent"
                }`}>
                  <motion.span
                    animate={{ 
                      textShadow: [
                        "0 0 10px hsl(277 78% 40% / 0.3)",
                        "0 0 20px hsl(277 78% 40% / 0.5)",
                        "0 0 10px hsl(277 78% 40% / 0.3)"
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    {feature.title}
                  </motion.span>
                </h3>
                <p className="text-foreground/80 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Solution;
